import {useEvent} from 'Event/EventProvider'
import {
  ObieOffer,
  useObieOffers,
} from 'lib/event-api/integrations/obie/offers/list'
import Select from 'lib/ui/Select'
import Option from 'lib/ui/Select/Option'
import {useOrganization} from 'organization/OrganizationProvider'
import React from 'react'

export interface OfferSelectProps {
  obieHubId: string
  value: string | null
  onChange: (obieOfferId: string) => void
  disabled?: boolean
}

export default function OfferSelect(props: OfferSelectProps) {
  const {obieHubId, value, onChange, disabled} = props
  const {event} = useEvent()
  const {client} = useOrganization()
  const {data: offers = [], isLoading, error} = useObieOffers({
    client,
    event,
    obieHubId,
  })

  if (isLoading) {
    return (
      <Select
        label="Offer"
        aria-label="offer"
        value=""
        onChange={() => {}}
        displayEmpty
        disabled
        fullWidth
      >
        <Option value="">Loading…</Option>
      </Select>
    )
  }

  if (error) {
    return (
      <Select
        label="Offer"
        aria-label="offer"
        helperText={error.message}
        error
        value=""
        onChange={() => {}}
        disabled
        fullWidth
      />
    )
  }

  if (offers.length === 0) {
    return (
      <Select
        label="Offer"
        aria-label="offer"
        value=""
        onChange={() => {}}
        displayEmpty
        disabled
        fullWidth
      >
        <Option value="">
          This AI Hub has no published offers. Publish one in Obie, or use Auto
          mode.
        </Option>
      </Select>
    )
  }

  return (
    <Select
      label="Offer"
      aria-label="offer"
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value as string)}
      disabled={disabled}
      fullWidth
    >
      {offers.map((offer) => (
        <Option key={offer.id} value={offer.id}>
          {getOfferLabel(offer)}
        </Option>
      ))}
    </Select>
  )
}

function getOfferLabel(offer: ObieOffer) {
  if (offer.payment_type === 'free' || offer.price_cents === null) {
    return `${offer.name} · Free`
  }

  const price = `$${(offer.price_cents / 100).toFixed(2)}`

  if (offer.billing_interval === 'month') {
    return `${offer.name} · ${price}/mo`
  }

  if (offer.billing_interval === 'year') {
    return `${offer.name} · ${price}/yr`
  }

  if (offer.installment_count !== null) {
    return `${offer.name} · ${price} × ${offer.installment_count}`
  }

  return `${offer.name} · ${price}`
}
